import clsx from 'clsx'
import { toast } from "react-toastify"
import React, { useState } from "react"

import { ActionButton } from "../../../components/utils/ActionButton"
import { PlusIcon } from "../../../resources/icons/PlusIcon"
import { DeleteIcon } from "../../../resources/icons/DeleteIcon"
import { InputRegistry } from "../../../utils/Interfaces"
import { G_onInputChangeHandler, G_ValidationLogic } from "../../../components/utils/InputHandlers"
import { RouteHydration } from '../../../utils/Helpers'
import { PEOPLE_PROFILE } from '../../../api/Registry'
import HttpServices from '../../../services/HttpServices'

export const InterstitialZone = ({ definition, tableReload }) => {
    const [state, setstate] = useState({
        posting: false,
        input: [],
        faults: [],
    })

    const inputRegistry: InputRegistry[] = [
        {
            display_type: 'input',
            name: 'label',
            title: 'Label',
            validation: 'alphanumeric',
        },
        {
            display_type: 'input',
            name: 'hint',
            title: 'Hint',
            validation: 'comments_descriptions',
        },
    ]

    const addNewEntry = () => {
        let { posting, input, faults } = state
        if (posting) return

        if (input.length === 10) {
            toast.info("To add a list larger than 10 items, use the upload feature")
            return
        }

        if (input.length > 0) {
            let hasError = false;
            const previousIndex = input.length - 1

            const previousEntry = input[previousIndex]
            const previousFault = faults[previousIndex]

            const updatedFaults = [...faults];

            if (previousFault.label.length > 0 || previousFault.hint.length > 0) {
                toast.info("Please correct the highlighted errors before adding a new entry.");

                return
            }

            for (const key in previousEntry) {
                const currentValue = previousEntry[key];
                const isMandatory = key === 'label';

                const syntheticEvent = {
                    target: {
                        name: `${key}_${previousIndex}`,
                        required: isMandatory,
                        value: typeof currentValue === 'string' ? currentValue.trim() : currentValue,
                    }
                };

                const whichInput = inputRegistry.find(
                    (registry: any) => registry.name === key
                )

                if (!whichInput) {
                    console.log(`Object Not Found: ${key}`)
                    return
                }

                let output: any = G_ValidationLogic(syntheticEvent, whichInput.validation, whichInput.title)
                console.log('output', output);

                if (output.error) {
                    hasError = true;
                    updatedFaults[previousIndex] = {
                        ...updatedFaults[previousIndex],
                        [key]: output.error
                    };
                }
            }

            if (hasError) {
                // Update state with the newly discovered faults so the UI turns Orange/Red
                setstate(prev => ({ ...prev, faults: updatedFaults }));
                toast.info("Please correct the highlighted errors before adding a new entry.");

                return;
            }

            // --- DUPLICATE CHECK ---
            const currentLabel = previousEntry.label.trim().toLowerCase();
            const isDuplicate = input.some((item, idx) =>
                idx !== previousIndex && item.label.trim().toLowerCase() === currentLabel
            );

            if (isDuplicate) {
                updatedFaults[previousIndex] = {
                    ...updatedFaults[previousIndex],
                    label: "This entry already exists in the list"
                };

                setstate(prev => ({ ...prev, faults: updatedFaults }));
                toast.info("Duplicate entry detected");

                return;
            }
        }

        setstate(prev => ({
            ...prev,
            input: [...prev.input, { label: "", hint: "" }],
            faults: [...prev.faults, { label: "", hint: "" }] // Grow faults alongside input
        }))
    }

    const removeEntry = (index: number) => {
        if (state.posting) return

        setstate(prev => ({
            ...prev,
            input: prev.input.filter((_, i) => i !== index),
            faults: prev.faults.filter((_, i) => i !== index)
        }))
    }

    const onChangeHandler = (e: any, key: string, index: number) => {
        const { posting } = state;
        if (posting) return;

        const { value } = e.target;
        let output: any = G_onInputChangeHandler(e);

        setstate(prev => {
            // Clone both arrays for immutability
            const newInputs = [...prev.input];
            const newFaults = [...prev.faults];

            newInputs[index] = {
                ...newInputs[index],
                [key]: value
            };

            /**
             * Reset the Fault for this specific key.
             * As soon as user starts typing, we hide the previous 
             * error message to show the system is "re-evaluating."
             */
            newFaults[index] = {
                ...newFaults[index],
                [key]: ""
            };

            return {
                ...prev,
                input: newInputs,
                faults: newFaults
            };
        });
    }

    const onInputBlur = (e: any, key: string, index: number) => {
        const whichInput = inputRegistry.find(registry => registry.name === key);
        if (!whichInput) return;

        const output: any = G_ValidationLogic(e, whichInput.validation, whichInput.title);

        setstate(prev => {
            const newInputs = [...prev.input];
            const newFaults = [...prev.faults];
            const processedValue = output.value !== undefined ? output.value : e.target.value.trim();

            newInputs[index] = {
                ...newInputs[index],
                [key]: processedValue
            };

            let errorMessage = output.error || "";

            if (key === 'label' && !errorMessage && processedValue) {
                const isDuplicate = checkForDuplicates(processedValue, index);
                if (isDuplicate) {
                    errorMessage = "This entry already exists in the list";
                }
            }

            newFaults[index] = {
                ...newFaults[index],
                [key]: errorMessage
            };

            return {
                ...prev,
                input: newInputs,
                faults: newFaults
            };
        });
    };

    const checkForDuplicates = (value: string, index: number,) => {
        const trimmedValue = value.trim().toLowerCase();
        if (!trimmedValue) return false;

        // Check if any other row has the same label
        return state.input.some((row, i) =>
            i !== index && row.label.trim().toLowerCase() === trimmedValue
        );
    };

    const onSubmitHandler = (e: any) => {
        e.preventDefault()

        let { input, posting } = state
        let hasValidationError = false

        if (posting) return

        let hasSystemErrors = false;
        const finalInputs = [...input];
        const finalFaults = Array(input.length).fill({ label: "", hint: "" });

        for (let index = 0; index < input.length; index++) {
            const entry = input[index];
            const rowFaults: any = {};

            for (const key in entry) {
                const whichInput = inputRegistry.find(reg => reg.name === key);
                if (!whichInput) continue;

                // Create the Synthetic Event for the Audit
                const syntheticEvent = {
                    target: {
                        name: `${key}_${index}`,
                        value: entry[key],
                        required: key === 'label'
                    }
                };

                // Run the same Global Logic used in onInputBlur
                const result = G_ValidationLogic(syntheticEvent, whichInput.validation, whichInput.title);

                if (result.error) {
                    hasSystemErrors = true;
                    rowFaults[key] = result.error;
                }

                if (key === 'label' && !rowFaults[key]) {
                    const isDuplicate = input.some((item, idx) =>
                        idx !== index && item.label.trim().toLowerCase() === entry.label.trim().toLowerCase()
                    );

                    if (isDuplicate) {
                        hasSystemErrors = true;
                        rowFaults[key] = "Duplicate entry detected";
                    }
                }
            }

            finalFaults[index] = rowFaults;
        }

        if (hasSystemErrors) {
            setstate(prev => ({ ...prev, faults: finalFaults }));
            toast.error("Please correct the highlighted errors before saving.");

            return;
        }

        setstate(prev => ({ ...prev, posting: true }));

        postPredefinedOptions()
    }

    const postPredefinedOptions = async () => {
        let { input, faults } = state
        let httpStatus = 200

        try {
            const uuid = definition.g
            let formData = new FormData()

            input.forEach((entry, index) => {
                // This syntax ensures the backend reconstitutes a single array of objects
                formData.append(`options[${index}].label`, entry.label);
                formData.append(`options[${index}].hint`, entry.hint || "");
            });

            const optionsAdditionRoute = `${RouteHydration(PEOPLE_PROFILE.DEFINITION_OPTIONS, ":definition", uuid)}`;
            const response: any = await HttpServices.httpPost(optionsAdditionRoute, formData)
            httpStatus = response.status

            if (httpStatus === 200) {
                toast.info(response.data.message)
                tableReload()
            } else {

            }
        } catch (error) {
            toast.error("Network error: Failed to save entries");
        } finally {
            setstate(prev => ({
                ...prev,
                posting: false,
                input: httpStatus === 200 ? [] : prev.input,
            }));
        }
    }

    return (
        <React.Fragment>
            <form onSubmit={onSubmitHandler}>
                {
                    state.input.length > 0 && state.input.map((row: any, index: number) => (
                        <div key={`input-map-${index}`} className=" dark:bg-slate-800/40 border-b border-orange-200 animate-pulse-once w-full flex flex-row align-middle items-center group">
                            {/* Label Input Column */}
                            <div className="px-3 py-2 flex-1">
                                <div className="relative block">
                                    <input type="text" autoComplete="off" value={row.label} name={`label_${index}`} placeholder="Label (e.g. Server Admin)"
                                        className={clsx(
                                            state.faults[index].label.length > 0 ?
                                                'text-red-600 ring-slate-300 dark:ring-slate-600 placeholder:text-red-400' :
                                                'text-slate-800 ring-slate-300 placeholder:text-slate-400 dark:placeholder:text-slate-400 dark:placeholder-slate-400 dark:text-white',
                                            'text-sm block w-full py-1 px-2 dark:bg-slate-800 disabled:cursor-not-allowed focus:outline-none'
                                        )} disabled={state.posting} onChange={(e) => onChangeHandler(e, 'label', index)} onBlur={(e) => onInputBlur(e, 'label', index)} required={true} />

                                    <div className="absolute inset-y-0 right-0 flex items-center w-8">
                                        {
                                            state.faults[index].label.length > 0 ? (
                                                <span className="fa-duotone text-red-500 fa-circle-exclamation fa-lg"></span>
                                            ) : null
                                        }
                                    </div>
                                </div>

                            </div>

                            <div className="px-3 py-2 flex-1">
                                <div className="relative block">
                                    <input type="text" autoComplete="off" value={row.hint} name={`hint_${index}`} placeholder="Hint (optional)"
                                        className={clsx(
                                            state.faults[index].hint.length > 0 ?
                                                'text-red-600 ring-slate-300 dark:ring-slate-600 placeholder:text-red-400' :
                                                'text-slate-800 ring-slate-300 placeholder:text-slate-400 dark:placeholder:text-slate-400 dark:placeholder-slate-400 dark:text-white',
                                            'text-sm block w-full py-1 px-2 dark:bg-slate-800 disabled:cursor-not-allowed focus:outline-none'
                                        )} disabled={state.posting} onChange={(e) => onChangeHandler(e, 'hint', index)} onBlur={(e) => onInputBlur(e, 'hint', index)} />

                                    <div className="absolute inset-y-0 right-0 flex items-center w-8">
                                        {
                                            state.faults[index].hint.length > 0 ? (
                                                <span className="fa-duotone text-red-500 fa-circle-exclamation fa-lg"></span>
                                            ) : null
                                        }
                                    </div>
                                </div>
                            </div>

                            <div className="px- py- text-right flex-none w-10 flex align-middle items-center justify-center">
                                <button onClick={() => removeEntry(index)} className="p-1 text-slate-400 transition-colors group-hover:text-red-500" title="Remove row">
                                    <span className="hidden group-hover:block">
                                        <DeleteIcon />
                                    </span>
                                </button>
                            </div>
                        </div>
                    ))
                }

                <div className="w-full flex flex-row">
                    <div className="flex-1 pt-3">
                        <ActionButton
                            variant="ghost"
                            callback={addNewEntry}
                            posting={state.posting}
                            actionName={
                                <div className="flex align-middle items-center gap-2">
                                    <PlusIcon />

                                    <span className="text-sm">New Entry</span>
                                </div>
                            }
                        />
                    </div>

                    <div className="flex-1 pt-3 flex flex-row-reverse">
                        {
                            state.input.length > 0 && (
                                <ActionButton
                                    type="submit"
                                    variant="primary"
                                    posting={state.posting}
                                    actionName={
                                        <div className="flex align-middle items-center gap-2">
                                            <span className="text-sm">Save</span>
                                        </div>
                                    }
                                />
                            )
                        }
                    </div>
                </div>

            </form>
        </React.Fragment>
    )
}